import React, { useState } from 'react';
import Table from 'cccisd-table';
import Modal from 'cccisd-modal';
import Toggle from 'cccisd-toggle';
import Tooltip from 'cccisd-tooltip';
import DatePicker from 'cccisd-datepicker';
import copy from 'copy-to-clipboard';
import IconCopy from 'cccisd-icons/copy3';
import axios from 'cccisd-axios';
import notification from 'cccisd-notification';

import _get from 'lodash/get';
import { isDateInPast, isMonday, getFormattedDateForDB, getPlanStatus, switchPlanNameIfNeeded } from '../helpers.js';
import style from './style.css';

const Boilerplate = window.cccisd && window.cccisd.boilerplate;

const getApiErrorMessage = (errors, fallbackMessage) => {
    if (!errors) return fallbackMessage;

    if (typeof errors === 'string') return errors;

    if (Array.isArray(errors)) {
        const firstArrayError = errors.find(item => typeof item === 'string');
        return firstArrayError || fallbackMessage;
    }

    if (typeof errors === 'object') {
        const firstValue = Object.values(errors)[0];
        if (typeof firstValue === 'string') return firstValue;
        if (Array.isArray(firstValue) && typeof firstValue[0] === 'string') return firstValue[0];
    }

    return fallbackMessage;
};

const YouthPlanCard = ({ plan, youth }) => {
    const originalStartDate = JSON.parse(plan.settings).startDate;
    const [isPlanCardOpened, setIsPlanCardOpened] = useState(false);
    const [autorenewToggle, setAutorenewToggle] = useState(JSON.parse(plan.settings).autoRenew);
    const [scheduledStartDate, setScheduledStartDate] = useState(originalStartDate);
    const planName = switchPlanNameIfNeeded(JSON.parse(plan.settings).planName);

    const getLocalSurveyUrl = surveyUrl => {
        if (!surveyUrl) return '';

        // surveyURL has the format of https://<domain>/<path>
        // change it so that the https://<domain> part is the same as the current domain,
        // so that it works in local dev environment

        const url = new URL(surveyUrl);
        return Boilerplate.url(url.pathname);
    };

    return (
        <div onClick={() => setIsPlanCardOpened(prev => !prev)} className={style.youth_plan_card_container}>
            <span
                className={`${style.plan_card_arrow}`}
                style={{ transform: isPlanCardOpened ? 'rotate(90deg)' : 'rotate(0deg)' }}
            >
                ▶
            </span>
            <span className={style.plan_card_header}>{planName}</span>

            <div className={style.plan_card_info_container}>
                <div>
                    <div className={style.plan_card_top_right_anchor}>
                        <div className={style.text_align_center}>
                            <h5 className={`${style.youth_plan_card_status} ${style.youth_card_field_info}`}>
                                {getPlanStatus(plan)}
                            </h5>
                        </div>
                        <br />
                        {!plan.isStopped && !plan.isClosed && (
                            <div
                                className={style.youth_plan_card_stop_button_container}
                                onClick={e => e.stopPropagation()}
                            >
                                <Modal
                                    trigger={
                                        <button
                                            type="button"
                                            className={`${style.youth_plan_card_stop_button} btn btn-danger`}
                                        >
                                            Stop Plan
                                        </button>
                                    }
                                    title="Confirm"
                                    size="medium"
                                    contentStyle={{ marginTop: '50px' }}
                                    onTop
                                    render={({ closeModal }) => (
                                        <div className={style.youth_plan_card_modal_content}>
                                            <div>
                                                All remaining surveys and messages will be stopped for{' '}
                                                <b>
                                                    {youth.fields.firstName} {youth.fields.lastName}
                                                </b>{' '}
                                                and their caregiver(s).
                                            </div>
                                            <div className={style.stop_plan_confirm_container}>
                                                <button
                                                    type="button"
                                                    onClick={() => closeModal()}
                                                    className="btn btn-warning"
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-secondary"
                                                    onClick={async () => {
                                                        const response = await axios.put(
                                                            Boilerplate.route('app.plan.update', {
                                                                plan: plan.assignmentPlanId,
                                                            }),
                                                            { stopPlan: true }
                                                        );

                                                        if (response.data.errors) {
                                                            notification({
                                                                message: getApiErrorMessage(
                                                                    response.data.errors,
                                                                    'Error deleting plan.'
                                                                ),
                                                                type: 'danger',
                                                            });
                                                            return Promise.reject(response.data.errors);
                                                        }

                                                        notification({
                                                            message: 'Plan stopped.',
                                                            duration: 5000,
                                                        });
                                                        window.location.reload();
                                                    }}
                                                >
                                                    Confirm
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                />
                            </div>
                        )}
                    </div>

                    <br />
                    <div>
                        <span className="form-label">Plan start date:</span>
                        <br />
                        <div onClick={e => e.stopPropagation()} className={style.plan_card_datepicker_container}>
                            {isDateInPast(scheduledStartDate) || plan.isClosed || plan.isStopped ? (
                                <div>{scheduledStartDate}</div>
                            ) : (
                                <DatePicker
                                    id="scheduledStartDate"
                                    selected={scheduledStartDate || null}
                                    filterDate={isMonday}
                                    onChange={val => setScheduledStartDate(getFormattedDateForDB(val))}
                                    placeholderText="-- Select a Monday to start --"
                                    dateFormat="MM/dd/yyyy"
                                />
                            )}
                        </div>

                        <br />
                        {!plan.isClosed && !plan.isStopped && (
                            <div
                                onClick={e => e.stopPropagation()}
                                className={style.plan_card_autorenew_toggle_container}
                            >
                                <span className="form-label">Auto-renew:</span>
                                <Toggle onChange={() => setAutorenewToggle(prev => !prev)} value={autorenewToggle} />
                            </div>
                        )}

                        {(autorenewToggle !== JSON.parse(plan.settings).autoRenew ||
                            scheduledStartDate !== originalStartDate) && (
                            <>
                                <br />
                                <button
                                    type="button"
                                    className={`${style.youth_plan_card_update_button} btn btn-warning`}
                                    onClick={async () => {
                                        const payload = {};
                                        if (autorenewToggle !== JSON.parse(plan.settings).autoRenew)
                                            payload.autoRenew = autorenewToggle;
                                        if (scheduledStartDate !== originalStartDate)
                                            payload.startDate = scheduledStartDate;

                                        const response = await axios.put(
                                            Boilerplate.route('app.plan.update', {
                                                plan: plan.assignmentPlanId,
                                            }),
                                            payload
                                        );

                                        if (response.data.errors) {
                                            notification({
                                                message: getApiErrorMessage(
                                                    response.data.errors,
                                                    'Error updating autorenew value.'
                                                ),
                                                type: 'danger',
                                            });
                                            return Promise.reject(response.data.errors);
                                        }

                                        notification({ message: 'Plan updated.', duration: 5000 });
                                        window.location.reload();
                                    }}
                                >
                                    Update Plan
                                </button>
                            </>
                        )}
                    </div>

                    {isPlanCardOpened && (
                        <div onClick={e => e.stopPropagation()}>
                            <br />
                            <div className="form-label">Surveys:</div>
                            <Table
                                rowKey="sessionKey"
                                columns={[
                                    {
                                        name: 'deployment.label',
                                        label: 'Label',
                                        sort: true,
                                        filter: true,
                                    },
                                    {
                                        name: 'wrapId',
                                        label: 'Wrap Id',
                                        sort: true,
                                        filter: true,
                                    },
                                    { name: 'completedDate', label: 'Completed Date' },
                                    {
                                        name: 'opensAtLocalTimezone',
                                        label: 'Survey Open Date',
                                        sort: true,
                                        filter: true,
                                        filterSettings: { type: 'date' },
                                    },
                                    {
                                        name: 'skipDueToReceiveMessagesToggle',
                                        label: 'Skipped Message?',
                                        sort: true,
                                        render: skipped => (
                                            <Tooltip
                                                title="Messages not sent due to disabled messages."
                                                placement="top"
                                            >
                                                <div>{skipped ? 'Yes' : ''}</div>
                                            </Tooltip>
                                        ),
                                    },
                                    {
                                        name: 'surveyUrl',
                                        label: 'Survey URL',
                                        sort: true,
                                        render: surveyUrl =>
                                            surveyUrl && (
                                                <div className={style.text_align_center}>
                                                    <Tooltip
                                                        title={
                                                            <>
                                                                <h5>Click to copy link:</h5>
                                                                <p style={{ marginBottom: 0 }}>{surveyUrl}</p>
                                                            </>
                                                        }
                                                    >
                                                        <button
                                                            type="button"
                                                            className="btn btn-xs btn-info"
                                                            onClick={() => {
                                                                copy(surveyUrl);
                                                                notification({
                                                                    message: 'Copied to clipboard!',
                                                                    type: 'success',
                                                                    duration: 4000,
                                                                });
                                                            }}
                                                        >
                                                            <IconCopy />
                                                        </button>
                                                    </Tooltip>
                                                </div>
                                            ),
                                    },
                                ]}
                                data={plan.sessionList.map(val => ({
                                    ...val,
                                    sessionKey: val.sessionId,
                                    'deployment.messageCount': val.deployment.messageCount.toString(),
                                    'deployment.label': val.deployment.label,
                                    skipDueToReceiveMessagesToggle: _get(
                                        val,
                                        'settings.skipDueToReceiveMessagesToggle',
                                        false
                                    ),
                                    surveyUrl: getLocalSurveyUrl(_get(val, 'sessionDeploymentInfo.surveyUrl', '')),
                                    // a caregiver no longer linked to the youth is not in wrapIds
                                    wrapId:
                                        (plan.wrapIds.find(w => w.pawnId === val.sessionDeploymentInfo.pawnId) || {})
                                            .wrapId || 'No longer linked',
                                    opensAtLocalTimezone: getFormattedDateForDB(new Date(val.opensAt)),
                                }))}
                                showRowActions={false}
                                hideShowingRowsSummary
                                showPerPageOptions={false}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default YouthPlanCard;
